"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PromotionScenario = exports.BasketEvaluationResult = exports.AuditTraceEntry = exports.AuditTraceStage = exports.EvaluatedLine = exports.AppliedPromotion = exports.BasketEvaluationContext = exports.BasketLineInput = exports.PromotionPatch = exports.PromotionCreate = exports.EvaluablePromotion = exports.PromotionSummary = exports.PromotionConfig = exports.CustomerTierConfig = exports.TimeBasedConfig = exports.CouponConfig = exports.CardCategoryApplicability = exports.BuyXGetYConfig = exports.BuyXGetYRewardOrder = exports.BUY_X_GET_Y_MAX_QUANTITY = exports.BUY_X_GET_Y_MAX_MINOR = exports.BuyXGetYScope = exports.GroupMixConfig = exports.GroupMixAllocation = exports.FixedPriceConfig = exports.FixedOffBasketConfig = exports.PercentageOffBasketConfig = exports.FixedOffLineConfig = exports.PercentageOffLineConfig = exports.LineFilter = exports.PromotionStatus = exports.PromotionScope = exports.PromotionKind = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * Promotion contracts (Sprint 10 — TRS §19).
 *
 * The promotion engine is a deterministic declarative pipeline
 * (TRS §19.2). The same basket + same promotions + same catalog
 * produces the same result on every terminal and every replay.
 * This contract file is the source of truth for both the BOS-side
 * TypeScript evaluator (in `apps/bos/backend/src/modules/promotions/`)
 * and the terminals-side Dart engine — the shared scenario JSON
 * under `packages/contracts/shared-scenarios/promotions/` pins
 * cross-implementation equivalence.
 *
 * Sprint 10 MVP rule set per TRS §19.5: percentage_off_line,
 * fixed_off_line, percentage_off_basket, fixed_off_basket, coupon
 * (gated wrapper), time_based (scheduled wrapper), customer_tier
 * (stub until Phase 2 loyalty lands). YUKI-236 adds the two multi-buy
 * shapes Loyalty campaigns use — fixed_price (special unit price) and
 * group_mix ("buy N, the cheapest K free", which covers N-for-M; RL-440 adds
 * "any N at a percent off the set") — and
 * card-category applicability on any promotion. RL-928 adds buy_x_get_y
 * (a trigger scope unlocks a benefit on units of a separate reward scope).
 * Bundle prices are not modelled.
 *
 * See `apps/bos/docs/modules/promotions.md` for the full spec.
 */
exports.PromotionKind = zod_1.z.enum([
    'percentage_off_line',
    'fixed_off_line',
    'percentage_off_basket',
    'fixed_off_basket',
    'coupon',
    'time_based',
    'customer_tier',
    'fixed_price',
    'group_mix',
    'buy_x_get_y',
]);
exports.PromotionScope = zod_1.z.enum(['line', 'basket', 'customer', 'tender']);
exports.PromotionStatus = zod_1.z.enum(['active', 'paused', 'archived']);
// ---------- Config-by-kind (discriminated) ----------
/**
 * Filter scope for line-targeted rules. EITHER `categoryIds` (rule
 * applies to lines whose item belongs to any of the listed
 * categories) OR `skuList` (rule applies to lines whose sku is in
 * the list) OR `tag` (rule applies to lines whose item carries the
 * named attribute). At most one filter type per rule — combining
 * them is Phase 2.
 */
exports.LineFilter = zod_1.z
    .object({
    categoryIds: zod_1.z.array(zod_1.z.string()).min(1).optional(),
    skuList: zod_1.z.array(zod_1.z.string()).min(1).optional(),
    tag: zod_1.z.string().min(1).optional(),
    /** A matched line is refused if it carries any excluded tag. */
    excludeTags: zod_1.z.array(zod_1.z.string().min(1)).min(1).optional(),
})
    .refine((v) => [v.categoryIds, v.skuList, v.tag].filter((x) => x !== undefined).length === 1, {
    message: 'exactly one of categoryIds / skuList / tag is required',
});
exports.PercentageOffLineConfig = zod_1.z
    .object({
    /** UI restriction, not bucket-wide promotion exclusivity (YUKI-294). */
    blocksManualDiscount: zod_1.z.boolean().optional(),
    /** Percent off, 1..99 — guards against 100% off, which is a giveaway, not a discount. */
    percent: zod_1.z.number().int().min(1).max(99),
    filter: exports.LineFilter,
})
    .strict();
exports.FixedOffLineConfig = zod_1.z
    .object({
    /** Fixed amount off per matched line, in minor units. */
    amountMinor: pricing_js_1.MoneyMinor,
    filter: exports.LineFilter,
    /** Maximum number of matching lines that receive the discount. */
    maxLines: zod_1.z.number().int().min(1).optional(),
})
    .strict();
exports.PercentageOffBasketConfig = zod_1.z
    .object({
    percent: zod_1.z.number().int().min(1).max(99),
    /** Optional min-spend gate, minor units. */
    minSpendMinor: pricing_js_1.MoneyMinor.optional(),
})
    .strict();
exports.FixedOffBasketConfig = zod_1.z
    .object({
    amountMinor: pricing_js_1.MoneyMinor,
    /** Required min-spend gate, minor units. */
    minSpendMinor: pricing_js_1.MoneyMinor,
})
    .strict();
/**
 * Special unit price (YUKI-236), Loyalty's `fixed_price` model: every matched
 * unit sells at `specialPriceMinor`. Per matched line the discount is
 * `max(0, unitPriceMinor - specialPriceMinor) * quantity`, rounded to the
 * minor unit the way `percentage_off_line` rounds a fractional quantity.
 * `minQuantity`, when set, gates the whole rule on the summed quantity of the
 * matched lines. A special price of 0 (free) is valid.
 */
exports.FixedPriceConfig = zod_1.z
    .object({
    blocksManualDiscount: zod_1.z.boolean().optional(),
    specialPriceMinor: pricing_js_1.MoneyMinor,
    filter: exports.LineFilter,
    minQuantity: zod_1.z.number().int().min(1).optional(),
})
    .strict();
/**
 * "Any N units from the scope, priced together" — Loyalty's `group_mix` model.
 * Two benefits are carried, exactly one per rule:
 *
 * - `freeCheapest` (YUKI-236): "buy N, the cheapest K are free".
 * - `percent` (RL-440): "any N for `percent` % off the set", e.g. any 3 from
 *   a range at 15 % per set. `allocation` says how each set's discount is
 *   spread over its units: `proportional` (default) by unit price, or
 *   `cheapest` onto the cheapest units first. Same total either way; the
 *   per-line breakdown differs, and refunds are computed from it.
 *
 * Evaluation, identical on both engines and on Loyalty's own engine:
 *
 * 1. Expand every matched line with an integral quantity into single units
 *    (a weighed, fractional-quantity line never joins a group).
 * 2. Sort units by unit price descending; ties by basket line index
 *    ascending. Form `floor(units / mixQuantity)` complete groups in that
 *    order; leftover units are not discounted.
 * 3a. `freeCheapest`: in each group the K cheapest units (price ascending,
 *    ties by line index ascending) are free — each adds its full unit price
 *    to its own line's discount.
 * 3b. `percent`: the group discount is `percent` % of the group's unit-price
 *    sum, half-up to a minor unit (a zero result discounts nothing).
 *    `proportional`: every unit but the last, in the step-2 order, takes
 *    `discount × price / sum` half-up, never more than is left of the group
 *    discount; the last takes what is left (never below zero). `cheapest`: units in price-ascending order (ties by line
 *    index ascending) each absorb up to their unit price until the group
 *    discount is spent. Each unit's share adds to its own line's discount.
 *
 * A line's accumulated discount is capped at its running total. Loyalty's
 * remaining group_mix benefits (`mixPrice`, `mixAmount`) are not expressible
 * here; the importer skips and reports them.
 *
 * **One unit from each group** — `setGroups` + `setPercent`. A set is not
 * "any N from the scope" but exactly one unit from each of the listed category
 * groups (`setGroups.length === mixQuantity`); three units from the same group
 * never form a set. Replaces steps 2–3b:
 *
 * 2'. Per group, the matched integral units whose line category is in that
 *    group, sorted by unit price descending, ties by line index ascending.
 *    `min(group sizes)` sets form; set `s` takes the `s`-th unit of every
 *    group. Leftover units are not discounted.
 * 3'. Each set is then priced exactly as 3b with `setPercent` in place of
 *    `percent`, its units ordered by unit price descending, ties by line
 *    index ascending.
 *
 * The discount is carried as `setPercent`, never `percent`, on purpose: a
 * reader that predates `setGroups` then finds no benefit and refuses the rule
 * instead of pricing it as "any N from the scope". Groups are disjoint and
 * each is a subset of `filter.categoryIds`.
 */
exports.GroupMixAllocation = zod_1.z.enum(['proportional', 'cheapest']);
exports.GroupMixConfig = zod_1.z
    .object({
    blocksManualDiscount: zod_1.z.boolean().optional(),
    mixQuantity: zod_1.z.number().int().min(2),
    freeCheapest: zod_1.z.number().int().min(1).optional(),
    percent: zod_1.z.number().int().min(1).max(99).optional(),
    setPercent: zod_1.z.number().int().min(1).max(99).optional(),
    setGroups: zod_1.z
        .array(zod_1.z.array(zod_1.z.string().min(1)).min(1))
        .min(2)
        .optional(),
    allocation: exports.GroupMixAllocation.optional(),
    filter: exports.LineFilter,
})
    .strict()
    .refine((v) => [v.freeCheapest, v.percent, v.setPercent].filter((b) => b !== undefined).length === 1, { message: 'exactly one of freeCheapest, percent or setPercent is required' })
    .refine((v) => (v.setPercent === undefined) === (v.setGroups === undefined), {
    message: 'setPercent and setGroups go together',
})
    .refine((v) => v.setGroups === undefined || v.setGroups.length === v.mixQuantity, {
    message: 'setGroups must name one group per unit of the set (mixQuantity)',
})
    .refine((v) => {
    if (v.setGroups === undefined)
        return true;
    const seen = new Set();
    for (const group of v.setGroups) {
        for (const id of new Set(group)) {
            if (seen.has(id))
                return false;
            seen.add(id);
        }
    }
    return true;
}, { message: 'setGroups must be disjoint' })
    .refine((v) => v.setGroups === undefined ||
    (v.filter.categoryIds !== undefined &&
        v.setGroups.every((group) => group.every((id) => v.filter.categoryIds.includes(id)))), { message: 'every setGroups category must be in filter.categoryIds' })
    .refine((v) => v.freeCheapest === undefined || v.freeCheapest < v.mixQuantity, {
    message: 'freeCheapest must be smaller than mixQuantity',
})
    .refine((v) => v.allocation === undefined || v.freeCheapest === undefined, {
    message: 'allocation applies only to a percent group mix',
});
/**
 * A buy-X-get-Y scope (RL-928): the lines whose sku is in `skuList` OR whose
 * category is in `categoryIds`. Either list or both; never neither. Back
 * Office writes `categoryIds` already expanded to every BOS category of each
 * named group's subtree, so the till compares the line's own category only.
 *
 * `excludeTags` (RL-950), as on a `LineFilter`: a line carrying any of these
 * item tags (BOS `segment` values) is not in the scope. Back Office writes the
 * same list on `trigger` and `reward`, so an excluded line neither triggers
 * the offer nor receives the reward (Loyalty LOY-444). A reader that does not
 * know the field would over-discount, so the importer emits it only behind
 * `REFORM_LOYALTY_BXGY_EXCLUDE_SEGMENTS`.
 */
exports.BuyXGetYScope = zod_1.z
    .object({
    skuList: zod_1.z.array(zod_1.z.string().min(1)).min(1).optional(),
    categoryIds: zod_1.z.array(zod_1.z.string().min(1)).min(1).optional(),
    excludeTags: zod_1.z.array(zod_1.z.string().min(1)).min(1).optional(),
})
    .strict()
    .refine((v) => v.skuList !== undefined || v.categoryIds !== undefined, {
    message: 'a scope needs skuList, categoryIds or both',
});
/**
 * The largest amount the till reads: Dart `int.tryParse` refuses anything above
 * the signed 64-bit maximum (RL-928).
 */
exports.BUY_X_GET_Y_MAX_MINOR = 9223372036854775807n;
/** The largest quantity a buy_x_get_y rule carries: a signed 32-bit int on every reader. */
exports.BUY_X_GET_Y_MAX_QUANTITY = 2_147_483_647;
/**
 * A non-negative amount of minor units as a base-10 digit string (no sign, no
 * whitespace), at most `BUY_X_GET_Y_MAX_MINOR`.
 */
const NonNegativeMinor = zod_1.z
    .string()
    .regex(/^\d+$/, 'non-negative integer minor units')
    // zod still runs a refinement after a failed regex, so only digits reach BigInt.
    .refine((v) => !/^\d+$/.test(v) || BigInt(v) <= exports.BUY_X_GET_Y_MAX_MINOR, {
    message: 'minor units above the signed 64-bit maximum',
});
const BuyXGetYQuantity = zod_1.z.number().int().min(1).max(exports.BUY_X_GET_Y_MAX_QUANTITY);
/**
 * The order reward units are picked in (RL-934): `cheapest` first (the
 * default when absent) or `dearest` first. Triggers are always dearest-first.
 */
exports.BuyXGetYRewardOrder = zod_1.z.enum(['cheapest', 'dearest']);
/**
 * "Buy X, get Y" (RL-928), Loyalty's `buy_x_get_y` model, sent with
 * `scope: 'basket'` and evaluated by the till only (YUKI-406); the BOS
 * evaluator does not price it. The shared rule and its test vectors are
 * re:form Loyalty's `test/vectors/buy-x-get-y.json` (LOY-435):
 *
 * 1. Per receipt, once. Lines are expanded to units; a weighed
 *    (fractional-quantity) line is one indivisible unit valued at its line
 *    amount.
 * 2. The `minQuantity` (absent = 1) dearest units in `trigger` unlock the
 *    offer, ties by line id; triggers stay dearest-first whatever
 *    `rewardOrder` says. Fewer means no benefit.
 * 3. The `getQuantity` (absent = 1) units in `reward` that were not taken as
 *    trigger units get the benefit, picked in `rewardOrder` (RL-934):
 *    `cheapest` (the default when absent) takes the cheapest eligible
 *    non-trigger units first, `dearest` the dearest first.
 *    In both orders, ties are broken by line id ascending.
 * 4. Per reward unit, exactly one benefit: `percentBp` basis points of the
 *    unit price, half up to the minor unit; `amountMinor` off, capped at the
 *    unit price; or `specialPriceMinor`, the unit sells at that price (never
 *    above its own). A weighed line is not a reward under a special price.
 *
 * `blocksManualDiscount` is the same UI restriction every line kind carries
 * (YUKI-294), not part of the pricing rule.
 */
exports.BuyXGetYConfig = zod_1.z
    .object({
    blocksManualDiscount: zod_1.z.boolean().optional(),
    trigger: exports.BuyXGetYScope,
    minQuantity: BuyXGetYQuantity.optional(),
    reward: exports.BuyXGetYScope,
    getQuantity: BuyXGetYQuantity.optional(),
    /** RL-934: which reward units get the benefit first. Absent = `cheapest`. */
    rewardOrder: exports.BuyXGetYRewardOrder.optional(),
    percentBp: zod_1.z.number().int().min(1).max(10_000).optional(),
    amountMinor: NonNegativeMinor.optional(),
    specialPriceMinor: NonNegativeMinor.optional(),
})
    .strict()
    .refine((v) => [v.percentBp, v.amountMinor, v.specialPriceMinor].filter((b) => b !== undefined).length === 1, { message: 'exactly one of percentBp, amountMinor or specialPriceMinor is required' });
/**
 * Card-category applicability (YUKI-236). Absent / null = the promotion is for
 * everyone. Otherwise it applies only when the evaluation context carries the
 * confirmed card's categories (`BasketEvaluationContext.cardCategories`):
 * `include`, when non-empty, needs at least one shared category; `exclude`
 * needs none shared. With `cardCategories` unknown (null — offline, or a card
 * not confirmed online) a promotion carrying either list is skipped.
 * Loyalty's `applicability.cardCategories` is include-only; `exclude` exists
 * for rules authored in BOS and for Loyalty growing one.
 */
exports.CardCategoryApplicability = zod_1.z
    .object({
    include: zod_1.z.array(zod_1.z.string().min(1)).max(100),
    exclude: zod_1.z.array(zod_1.z.string().min(1)).max(100),
})
    .strict();
/**
 * Coupon-gated wrapper. The `inner` field is a recursive
 * promotion config of any line/basket kind; the evaluator only
 * applies the inner when the basket carries a matching coupon
 * code.
 */
exports.CouponConfig = zod_1.z
    .object({
    inner: zod_1.z.discriminatedUnion('kind', [
        zod_1.z.object({ kind: zod_1.z.literal('percentage_off_line'), config: exports.PercentageOffLineConfig }),
        zod_1.z.object({ kind: zod_1.z.literal('fixed_off_line'), config: exports.FixedOffLineConfig }),
        zod_1.z.object({ kind: zod_1.z.literal('percentage_off_basket'), config: exports.PercentageOffBasketConfig }),
        zod_1.z.object({ kind: zod_1.z.literal('fixed_off_basket'), config: exports.FixedOffBasketConfig }),
    ]),
})
    .strict();
/**
 * Time-based wrapper. Day-of-week mask (0=Sun, 6=Sat) plus an
 * optional time-of-day window in HH:MM (24h, terminal-local time).
 */
exports.TimeBasedConfig = zod_1.z
    .object({
    inner: zod_1.z.discriminatedUnion('kind', [
        zod_1.z.object({ kind: zod_1.z.literal('percentage_off_line'), config: exports.PercentageOffLineConfig }),
        zod_1.z.object({ kind: zod_1.z.literal('fixed_off_line'), config: exports.FixedOffLineConfig }),
        zod_1.z.object({ kind: zod_1.z.literal('percentage_off_basket'), config: exports.PercentageOffBasketConfig }),
        zod_1.z.object({ kind: zod_1.z.literal('fixed_off_basket'), config: exports.FixedOffBasketConfig }),
    ]),
    daysOfWeek: zod_1.z.array(zod_1.z.number().int().min(0).max(6)).min(1).max(7),
    /** HH:MM 24h start (terminal-local). null = midnight. */
    startTime: zod_1.z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullable(),
    /** HH:MM 24h end (terminal-local). null = midnight next day. */
    endTime: zod_1.z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullable(),
})
    .strict();
/**
 * Customer-tier stub. Tier name + percent off. Sprint 10 ships
 * this as a no-op stub — the evaluator never matches it until
 * Phase 2 customer table lands.
 */
exports.CustomerTierConfig = zod_1.z
    .object({
    tierCode: zod_1.z.string().min(1).max(64),
    percent: zod_1.z.number().int().min(1).max(99),
})
    .strict();
/**
 * Discriminated config by kind. Used both as the persisted JSON
 * column shape and as the REST write-body validator.
 */
exports.PromotionConfig = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('percentage_off_line'), config: exports.PercentageOffLineConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('fixed_off_line'), config: exports.FixedOffLineConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('percentage_off_basket'), config: exports.PercentageOffBasketConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('fixed_off_basket'), config: exports.FixedOffBasketConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('coupon'), config: exports.CouponConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('time_based'), config: exports.TimeBasedConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('customer_tier'), config: exports.CustomerTierConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('fixed_price'), config: exports.FixedPriceConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('group_mix'), config: exports.GroupMixConfig }),
    zod_1.z.object({ kind: zod_1.z.literal('buy_x_get_y'), config: exports.BuyXGetYConfig }),
]);
// ---------- Read shape ----------
exports.PromotionSummary = zod_1.z.object({
    id: zod_1.z.string(),
    code: zod_1.z.string(),
    displayName: zod_1.z.string(),
    description: zod_1.z.string().nullable(),
    kind: exports.PromotionKind,
    scope: exports.PromotionScope,
    priority: zod_1.z.number().int(),
    config: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    couponCode: zod_1.z.string().nullable(),
    startsAt: zod_1.z.string().datetime().nullable(),
    endsAt: zod_1.z.string().datetime().nullable(),
    exclusiveWithinBucket: zod_1.z.boolean(),
    stackableWithCustomerDiscount: zod_1.z.boolean(),
    excludesPromotionCodes: zod_1.z.array(zod_1.z.string()),
    /** Null = for everyone. See `CardCategoryApplicability`. */
    cardCategories: exports.CardCategoryApplicability.nullable().optional(),
    /**
     * EAT-3: the rule needs the till to be online. A till that is offline (or
     * cannot tell) skips it, so a discount that depends on an online check — a
     * loyalty card's category, say — is never given offline. Absent on an older
     * BOS = `false`. BOS's own evaluator runs online and ignores it.
     */
    onlineOnly: zod_1.z.boolean().optional(),
    status: exports.PromotionStatus,
    version: zod_1.z.number().int().min(1),
    /**
     * Provenance (YUKI-176). `loyalty` rows are imported from re:form Loyalty and
     * read-only in BOS; `connector` rows (RL-467) are imported from a store
     * system and read-only too. Absent on an older BOS = `bos`.
     */
    source: zod_1.z.enum(['bos', 'loyalty', 'connector']).optional(),
    /** The upstream campaign id of an imported row. */
    externalId: zod_1.z.string().nullable().optional(),
    /**
     * RL-467: the one location the promotion applies at; null / absent = every
     * location. Terminals of other locations receive it as a delete tombstone.
     */
    locationId: zod_1.z.string().nullable().optional(),
    importedAt: zod_1.z.string().datetime().nullable().optional(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
/**
 * A promotion as the evaluator sees it: the read shape minus the admin-only
 * fields the pipeline never reads (`description`, `createdAt`, `updatedAt`).
 * A `PromotionSummary` satisfies it, and so does a rule a terminal rebuilt
 * from the sync feed or a shared scenario, which carry none of the three.
 */
exports.EvaluablePromotion = exports.PromotionSummary.partial({
    description: true,
    createdAt: true,
    updatedAt: true,
});
// ---------- Write shapes ----------
const PromotionCommon = zod_1.z.object({
    code: zod_1.z.string().min(1).max(64),
    displayName: zod_1.z.string().min(1).max(255),
    description: zod_1.z.string().max(1000).optional(),
    scope: exports.PromotionScope,
    priority: zod_1.z.number().int().min(0).max(10000).optional(),
    couponCode: zod_1.z.string().min(1).max(64).nullable().optional(),
    startsAt: zod_1.z.string().datetime().nullable().optional(),
    endsAt: zod_1.z.string().datetime().nullable().optional(),
    exclusiveWithinBucket: zod_1.z.boolean().optional(),
    stackableWithCustomerDiscount: zod_1.z.boolean().optional(),
    excludesPromotionCodes: zod_1.z.array(zod_1.z.string()).max(50).optional(),
    cardCategories: exports.CardCategoryApplicability.nullable().optional(),
    /** EAT-3: skip the rule on a till that is offline. Default `false`. */
    onlineOnly: zod_1.z.boolean().optional(),
});
exports.PromotionCreate = zod_1.z.intersection(PromotionCommon, exports.PromotionConfig);
/**
 * PATCH body. Patches non-typing fields only; to retype a
 * promotion (change kind / config), archive + create — the patch
 * surface stays simple and the audit story stays clean.
 */
exports.PromotionPatch = zod_1.z
    .object({
    expectedVersion: zod_1.z.number().int().min(1),
    displayName: zod_1.z.string().min(1).max(255).optional(),
    description: zod_1.z.string().max(1000).nullable().optional(),
    priority: zod_1.z.number().int().min(0).max(10000).optional(),
    couponCode: zod_1.z.string().min(1).max(64).nullable().optional(),
    startsAt: zod_1.z.string().datetime().nullable().optional(),
    endsAt: zod_1.z.string().datetime().nullable().optional(),
    exclusiveWithinBucket: zod_1.z.boolean().optional(),
    stackableWithCustomerDiscount: zod_1.z.boolean().optional(),
    excludesPromotionCodes: zod_1.z.array(zod_1.z.string()).max(50).optional(),
    /** Replace the card-category applicability; null = for everyone. */
    cardCategories: exports.CardCategoryApplicability.nullable().optional(),
    /** EAT-3: set or clear "online only". Absent = unchanged. */
    onlineOnly: zod_1.z.boolean().optional(),
    /** Replace the entire (kind, config) — caller must keep kind. */
    config: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
})
    .strict();
// ---------- Evaluator I/O (TRS §19.2) ----------
/**
 * Input basket line — minimum the evaluator needs.
 */
exports.BasketLineInput = zod_1.z.object({
    sku: zod_1.z.string(),
    /** Server / engine fills `name` from catalog lookup. */
    name: zod_1.z.string(),
    categoryId: zod_1.z.string().nullable(),
    tags: zod_1.z.array(zod_1.z.string()).optional(),
    quantity: zod_1.z.number().positive(),
    unitPriceMinor: pricing_js_1.MoneyMinor,
    taxCategory: zod_1.z.string(),
});
exports.BasketEvaluationContext = zod_1.z.object({
    basket: zod_1.z.object({ lines: zod_1.z.array(exports.BasketLineInput).min(1) }),
    /** Active rules (the evaluator filters by status + window). */
    promotions: zod_1.z.array(exports.EvaluablePromotion),
    /** Caller-supplied evaluation timestamp (ISO). */
    evaluatedAt: zod_1.z.string().datetime(),
    /** Optional coupon codes the basket carries. */
    couponCodes: zod_1.z.array(zod_1.z.string()).optional(),
    /** Optional customer tier (Phase 2 fully wired). */
    customerTier: zod_1.z.string().nullable().optional(),
    /**
     * The attached card's categories, as confirmed online by the card lookup.
     * Null / absent = unknown (offline, or a card not confirmed online): every
     * promotion with `cardCategories` is skipped. The caller decides what a
     * basket with no card at all passes — see YUKI-133.
     */
    cardCategories: zod_1.z.array(zod_1.z.string()).nullable().optional(),
});
exports.AppliedPromotion = zod_1.z.object({
    promotionId: zod_1.z.string(),
    code: zod_1.z.string(),
    ruleVersion: zod_1.z.number().int(),
    /** Discount applied to this line / basket, in minor units, positive. */
    discountMinor: pricing_js_1.MoneyMinor,
});
exports.EvaluatedLine = zod_1.z.object({
    sku: zod_1.z.string(),
    name: zod_1.z.string(),
    quantity: zod_1.z.number(),
    unitPriceMinor: pricing_js_1.MoneyMinor,
    /** After all line-scope promotions, before basket-level. */
    lineTotalMinor: pricing_js_1.MoneyMinor,
    appliedPromotions: zod_1.z.array(exports.AppliedPromotion),
    taxCategory: zod_1.z.string(),
});
exports.AuditTraceStage = zod_1.z.enum([
    'RESOLVE',
    'FILTER',
    'PARTITION',
    'PRIORITISE',
    'APPLY_LINE',
    'APPLY_BASKET',
    'APPLY_CUSTOMER',
    'APPLY_TENDER',
    'ROUND_AND_TOTALISE',
]);
exports.AuditTraceEntry = zod_1.z.object({
    stage: exports.AuditTraceStage,
    promotionId: zod_1.z.string(),
    promotionCode: zod_1.z.string(),
    decision: zod_1.z.enum([
        'applied',
        'suppressed',
        'not_matched',
        'out_of_window',
        'coupon_missing',
        /** Card-category promotion, card categories unknown (YUKI-236). */
        'card_unconfirmed',
        /** Card-category promotion, the confirmed card does not qualify. */
        'card_category_mismatch',
    ]),
    /** When suppressed, the promotion id that won. */
    suppressedBy: zod_1.z.string().nullable().optional(),
    /** Free-form note (for not_matched filter reasons). */
    note: zod_1.z.string().optional(),
});
exports.BasketEvaluationResult = zod_1.z.object({
    lines: zod_1.z.array(exports.EvaluatedLine),
    subtotalMinor: pricing_js_1.MoneyMinor,
    totalDiscountMinor: pricing_js_1.MoneyMinor,
    totalMinor: pricing_js_1.MoneyMinor,
    trace: zod_1.z.array(exports.AuditTraceEntry),
});
/**
 * Shared-scenario file shape. Sprint 10 ships ~50 cases under
 * `packages/contracts/shared-scenarios/promotions/*.json`; the
 * BOS-side evaluator vitest + the Dart-side engine test harness
 * both load every file and assert the result matches the
 * `expected` block.
 */
exports.PromotionScenario = zod_1.z.object({
    name: zod_1.z.string().min(1),
    notes: zod_1.z.string(),
    context: exports.BasketEvaluationContext,
    expected: exports.BasketEvaluationResult,
});
//# sourceMappingURL=promotions.js.map